<template>
  <div class="events">
    <div class="events__wrapper">
      <div
        class="events__header-background"
        :style="{
          backgroundImage: `url(${featuredEvent.header_image})`,
        }"
      ></div>
      <div class="events__main-container">
        <div v-if="featuredEvent" class="events__details-box">
          <span class="events__details-box_data"
            >{{ featuredEvent.dates }} | {{ featuredEvent.exhibition_type }}</span
          >
          <h2 class="events__details-box_title">{{ featuredEvent.title }}</h2>
          <span class="events__details-box_details">{{ featuredEvent.description }}</span>
          <BaseButton
            class="button button--xs button--white"
            text="MORE INFORMATION"
            @click="$router.push({ path: `/events/${featuredEvent.slug}` })"
          />
        </div>
        <EventsTitleBox
          title="Events"
          :tagsData="tagsData"
          :sortData="sortData"
          :selectedSort="cardData.sort"
          @changeFilter="changeFilterValue"
          @sortChange="sortEvents($event.id)"
        />
        <EventsList
          v-if="eventsList && eventsList.length"
          :items="eventsList"
          routePrefix="events"
          :useBackgroundImage="true"
        />
        <h6 class="events__empty" v-else>No events</h6>

        <div class="events__button-box" v-if="showLoadMoreButton && hasMorePages">
          <BaseButton
            class="button button--xs button--white"
            text="LOAD MORE"
            @click.native="handleLoadMoreClick"
          />
        </div>
      </div>
    </div>
    <div ref="infiniteTrigger" style="height: 1px"></div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import BaseButton from "@/components/buttons/BaseButton.vue";
import useInfiniteScroll from "@/composables/useInfiniteScroll.js";
import EventsTitleBox from "@/views/public/events/components/EventsTitleBox.vue";
import EventsList from "@/views/public/events/components/EventsList.vue";
import useSeoHead from "@/composables/useSeoHead";

export default {
  components: { BaseButton, EventsTitleBox, EventsList },

  data() {
    return {
      sortData: [
        { id: "newest_to_oldest", title: "Newest to Oldest" },
        { id: "oldest_to_newest", title: "Oldest to Newest" },
      ],
      filterBlock: {
        list: "events",
        type: "collection",
      },
      cardData: {
        sort: "newest_to_oldest",
        page: 1,
        events: "",
      },
      showLoadMoreButton: true,
      loading: false,
    };
  },
  head() {
    return useSeoHead({
      title: "Events browngrotta arts",
      description: "Description Events browngrotta arts",
      image: "/img/icons/favicon-500x500.png",
      routePath: this.$route.path,
    });
  },
  async created() {
    await this.apiGetFiltersBlock(this.filterBlock);
    await this.apiGetEventsList(this.cardData);
    await this.apiGetFeaturedEvent();

    const breadcrumbs = [
      {
        title: "Home",
        to: "/",
      },
      {
        title: "Events",
        to: "/events/events",
        clickable: false,
      },
    ];
    this.setBreadcrumbs(breadcrumbs);
  },

  watch: {
    tagsData: {
      handler: function (val, oldVal) {
        let events = "";
        val.forEach(el => {
          if (el.selected) {
            events = el.key === "" ? "" : el.key;
          }
        });

        this.cardData.events = events;
        this.selectTypes(events);
      },
      deep: true,
    },
  },

  computed: {
    ...mapGetters("publicapi/events", {
      eventsList: "getEventsList",
      hasMorePages: "getHasMorePages",
      featuredEvent: "getFeaturedEvent",
      tagsData: "getFilters",
    }),
  },

  methods: {
    ...mapActions("publicapi/events", [
      "apiGetFeaturedEvent",
      "apiGetEventsList",
      "apiGetFiltersBlock",
    ]),
    ...mapMutations("publicapi/events", ["changeFilterValue"]),
    ...mapMutations(["setBreadcrumbs"]),

    async loadMore() {
      if (this.loading || !this.hasMorePages) return;
      this.loading = true;

      let data = { ...this.cardData, page: this.cardData.page + 1 };
      let success = await this.apiGetEventsList(data);
      if (success) {
        this.cardData = data;
      }
      this.loading = false;
    },

    handleLoadMoreClick() {
      this.showLoadMoreButton = false;
      this.loadMore();
      this.infiniteScroll.enableInfiniteScroll();
    },

    handleScroll() {
      const scrollPosition = window.innerHeight + window.scrollY;
      const bottomPosition = document.documentElement.scrollHeight;

      if (scrollPosition >= bottomPosition - 100) {
        this.loadMore();
      }
    },

    async selectTypes(events) {
      let data = { ...this.cardData, page: 1, events: events };
      let success = await this.apiGetEventsList(data);
      if (success) {
        this.cardData = data;
      }
    },

    async sortEvents(id) {
      let data = { ...this.cardData, page: 1, sort: id };
      let success = await this.apiGetEventsList(data);
      if (success) {
        this.cardData = data;
      }
    },
  },
  mounted() {
    this.infiniteScroll = useInfiniteScroll(this);
    if (!this.showLoadMoreButton) {
      this.infiniteScroll.enableInfiniteScroll();
    }
  },
  beforeDestroy() {
    this.infiniteScroll.disableInfiniteScroll();
  },
};
</script>

<style lang="scss" scoped>
* {
  color: $white;
  font-size: 18px;
  line-height: 1.3;
  @media screen and (max-width: $xs) {
    font-size: 16px;
    line-height: 1.5;
  }
}

.events {
  display: flex;

  &__header {
    &-background {
      @include backgroundBlock();
    }
  }

  &__wrapper {
    @include fullScreenWrapper;
  }

  &__main-container {
    @include mainContainerCenteredCol(0rem 10.5rem 23.5rem, 40rem);

    @media screen and (max-width: $md) {
      margin-top: 24.5rem;
    }

    @media screen and (max-width: $xs) {
      padding: 0rem 2rem 24rem;
    }
  }

  &__details-box {
    display: flex;
    flex-direction: column;
    width: 55%;
    @media screen and (max-width: $md) {
      width: 100%;
    }

    &_data {
      font-size: 16px;
      line-height: 1.6;
      text-transform: uppercase;
      @media screen and (max-width: $xs) {
        font-size: 14px;
      }
    }

    &_title {
      font-size: 40px;
      line-height: 1.5;
      margin-bottom: 17px;
      @media screen and (max-width: $xs) {
        font-size: 35px;
        line-height: 1.23;
        margin-bottom: 25px;
      }
    }

    .button {
      margin-top: 22px;
      @media screen and (max-width: $xs) {
        margin-top: 35px;
      }
    }
  }

  &__empty {
    @include emptyMessage();
  }

  &__button-box {
    @include centeredButtonBox();
  }
}
</style>

